// ============================================================
// Form Produk Kasir — halaman tambah / edit
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

function emptyForm() {
  return {
    name: '', slug: '', sku: '', description: '', base_price: 0,
    app_price: null, web_price: null, inko_price: null, surang_price: null,
    cost_price: 0,
    price_unit: 'pcs', min_order: 1, stock: 0, category_id: '', status: 'active',
    is_custom_size: false, surang_enabled: false, price_per_m2: null,
    app_price_per_m2: null, web_price_per_m2: null, surang_price_per_m2: null, inko_price_per_m2: null, weight: null,
    dimension_length: null, dimension_width: null, dimension_height: null,
    reward_points: 0, variants: [],
  };
}

function normalizeVariants(variants) {
  const list = Array.isArray(variants) ? variants : Object.values(variants || {});
  return list.map((v, i) => ({
    category_variant_option_id: v.category_variant_option_id || '',
    type: v.type || '',
    value: v.value || '',
    price_modifier: Number(v.price_modifier) || 0,
    sort_order: v.sort_order ?? i,
  }));
}

function normalizeGallery(items) {
  const list = Array.isArray(items) ? items : [];
  return list.map((item, i) => ({
    id: item.id,
    type: item.type || 'photo',
    url: item.url || item.thumbnail_url || '',
    thumbnail_url: item.thumbnail_url || item.url || '',
    sort_order: item.sort_order ?? i,
    is_primary: !!item.is_primary,
    _pending: false,
    _file: null,
    _preview: null,
  }));
}

function productToForm(d) {
  if (!d) return emptyForm();
  return {
    name: d.name || '',
    slug: d.slug || '',
    sku: d.sku || '',
    description: d.description || '',
    base_price: Number(d.base_price) || 0,
    app_price: d.app_price != null && d.app_price !== '' ? Number(d.app_price) : null,
    web_price: d.web_price != null && d.web_price !== '' ? Number(d.web_price) : null,
    inko_price: d.inko_price != null && d.inko_price !== '' ? Number(d.inko_price) : null,
    surang_price: d.surang_price != null && d.surang_price !== '' ? Number(d.surang_price) : null,
    cost_price: Number(d.cost_price) || 0,
    price_unit: d.price_unit || 'pcs',
    min_order: Number(d.min_order) || 1,
    stock: Number(d.stock) || 0,
    category_id: d.category_id ? String(d.category_id) : '',
    status: d.status || 'active',
    is_custom_size: !!d.is_custom_size,
    surang_enabled: !!d.surang_enabled,
    price_per_m2: d.price_per_m2 != null ? Number(d.price_per_m2) : null,
    app_price_per_m2: d.app_price_per_m2 != null ? Number(d.app_price_per_m2) : null,
    web_price_per_m2: d.web_price_per_m2 != null ? Number(d.web_price_per_m2) : null,
    surang_price_per_m2: d.surang_price_per_m2 != null ? Number(d.surang_price_per_m2) : null,
    inko_price_per_m2: d.inko_price_per_m2 != null ? Number(d.inko_price_per_m2) : null,
    weight: d.weight != null ? Number(d.weight) : null,
    dimension_length: d.dimension_length != null ? Number(d.dimension_length) : null,
    dimension_width: d.dimension_width != null ? Number(d.dimension_width) : null,
    dimension_height: d.dimension_height != null ? Number(d.dimension_height) : null,
    reward_points: Number(d.reward_points) || 0,
    variants: normalizeVariants(d.variants),
  };
}

function readPageConfig(el) {
  if (el) {
    const raw = el.getAttribute('data-config');
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.error('kasirProductForm: data-config invalid', e);
      }
    }
  }
  return null;
}

function extractProductIdFromUrl() {
  const m = window.location.pathname.match(/\/products\/([^/]+)\/edit\/?$/);
  return m ? m[1] : null;
}

function createKasirProductForm() {
  return {
    saving: false,
    loading: false,
    productId: extractProductIdFromUrl(),
    categories: [],
    indexUrl: '/',
    editUrlBase: '',
    form: emptyForm(),
    imageFile: null,
    imagePreview: null,
    removeImage: false,
    gallery: [],
    galleryRemoveIds: [],
    galleryPrimaryId: null,
    categoryVariantTemplate: [],
    canEditCostPrice: false,
    productReviews: [],
    reviewsLoading: false,

    get tracksStock() {
      return ['pcs', 'lembar', 'helai'].includes(this.form.price_unit);
    },

    get categoryOptionChoices() {
      const choices = [];
      (this.categoryVariantTemplate || []).forEach((t) => {
        (t.options || []).forEach((o) => {
          choices.push({
            id: o.id,
            type: t.type,
            type_label: t.label || t.type,
            value: o.value,
            default_price_modifier: Number(o.default_price_modifier) || 0,
            specification: o.specification || '',
          });
        });
      });
      return choices;
    },

    get hasCategoryVariants() {
      return this.categoryOptionChoices.length > 0;
    },

    applyProduct(d) {
      if (!d) return;
      this.productId = d.id || this.productId;
      Object.assign(this.form, productToForm(d));
      this.gallery = normalizeGallery(d.gallery || []);
      this.galleryRemoveIds = [];
      this.galleryPrimaryId = this.gallery.find((g) => g.is_primary)?.id
        || this.gallery.find((g) => g.type === 'photo')?.id
        || null;
      if (d.main_image_url) {
        this.imagePreview = d.main_image_url;
      }
      this.imageFile = null;
      this.removeImage = false;
      this.loadCategoryVariantTemplate(this.form.category_id);
    },

    async loadCategoryVariantTemplate(categoryId) {
      if (!categoryId) {
        this.categoryVariantTemplate = [];
        return;
      }

      const cat = this.categories.find((c) => String(c.id) === String(categoryId));
      if (cat?.variant_types?.length) {
        this.categoryVariantTemplate = cat.variant_types;
        return;
      }

      try {
        const { data } = await axios.get('/api/v1/kasir/products/meta/category-variants/' + categoryId);
        this.categoryVariantTemplate = data.data || [];
      } catch (e) {
        this.categoryVariantTemplate = [];
      }
    },

    async onCategoryChange() {
      const prev = this.form.category_id;
      await this.loadCategoryVariantTemplate(this.form.category_id);

      if (this.form.variants.length > 0 && this.hasCategoryVariants) {
        const ok = await window.confirmDialog?.({
          title: 'Ganti Kategori?',
          message: 'Varian produk saat ini mungkin tidak sesuai kategori baru. Muat ulang dari template kategori?',
          confirmText: 'Muat Template',
          cancelText: 'Biarkan',
          variant: 'warning',
        });
        if (ok) {
          this.loadAllFromCategory();
        }
      } else if (this.form.variants.length === 0 && this.hasCategoryVariants) {
        this.loadAllFromCategory();
      }

      void prev;
    },

    async fetchProduct(id) {
      if (!id) return;
      this.loading = true;
      try {
        const { data } = await axios.get('/api/v1/kasir/products/' + id);
        this.applyProduct(data.data);
        await this.loadProductReviews();
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal memuat data produk.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async init() {
      const cfg = readPageConfig(this.$el) || {};
      this.categories = cfg.categories || [];
      this.indexUrl = cfg.indexUrl || '/';
      this.editUrlBase = cfg.editUrlBase || this.indexUrl.replace(/\/$/, '');
      this.canEditCostPrice = !!cfg.canEditCostPrice;
      this.productId = cfg.productId || this.productId;

      if (cfg.product) {
        this.applyProduct(cfg.product);
      }

      if (!this.form.name && this.productId) {
        await this.fetchProduct(this.productId);
      } else if (this.productId) {
        await this.loadProductReviews();
      }

      if (this.form.price_unit === 'meter') {
        this.form.is_custom_size = true;
      }

      await this.loadCategoryVariantTemplate(this.form.category_id);

      this.$watch('form.price_unit', (unit) => {
        if (unit === 'meter') {
          this.form.is_custom_size = true;
        }
      });
    },

    async loadProductReviews() {
      if (!this.productId) return;
      this.reviewsLoading = true;
      try {
        const { data } = await axios.get(`/api/v1/kasir/products/${this.productId}/reviews`, {
          params: { per_page: 30 },
        });
        this.productReviews = (data.data || []).map((rev) => ({
          ...rev,
          _replyDraft: rev.admin_reply || '',
          _replySaving: false,
        }));
      } catch {
        this.productReviews = [];
      } finally {
        this.reviewsLoading = false;
      }
    },

    async replyReview(rev) {
      const reply = (rev._replyDraft || '').trim();
      if (!reply) {
        window.toast?.('Balasan wajib diisi.', 'error');
        return;
      }
      rev._replySaving = true;
      try {
        const { data } = await axios.post(`/api/v1/kasir/products/reviews/${rev.id}/reply`, { reply });
        Object.assign(rev, data.data || {});
        rev._replyDraft = rev.admin_reply || reply;
        window.toast?.(data.message || 'Balasan tersimpan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan balasan.', 'error');
      } finally {
        rev._replySaving = false;
      }
    },

    onImageSelect(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      this.imageFile = file;
      this.removeImage = false;
      this.imagePreview = URL.createObjectURL(file);
    },

    clearImage() {
      this.imageFile = null;
      this.imagePreview = null;
      this.removeImage = true;
    },

    onGallerySelect(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      files.forEach((file) => {
        const type = file.type.startsWith('video/') ? 'video' : 'photo';
        const preview = URL.createObjectURL(file);
        this.gallery.push({
          id: 'tmp-' + Date.now() + '-' + Math.random().toString(36).slice(2),
          type,
          url: preview,
          thumbnail_url: type === 'photo' ? preview : null,
          sort_order: this.gallery.length,
          is_primary: false,
          _pending: true,
          _file: file,
          _preview: preview,
        });
      });

      e.target.value = '';
    },

    removeGalleryItem(item) {
      if (!item._pending) {
        this.galleryRemoveIds.push(item.id);
      }
      if (this.galleryPrimaryId === item.id) {
        this.galleryPrimaryId = null;
      }
      this.gallery = this.gallery.filter((g) => g.id !== item.id);
    },

    setGalleryPrimary(item) {
      if (item.type !== 'photo') {
        window.toast('Hanya foto yang bisa dijadikan highlight galeri.', 'warning');
        return;
      }
      this.galleryPrimaryId = item.id;
      this.gallery = this.gallery.map((g) => ({
        ...g,
        is_primary: g.id === item.id,
      }));
    },

    moveGalleryItem(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.gallery.length) return;
      const items = [...this.gallery];
      const [moved] = items.splice(index, 1);
      items.splice(target, 0, moved);
      this.gallery = items.map((item, i) => ({ ...item, sort_order: i }));
    },

    addVariant() {
      this.form.variants.push({
        category_variant_option_id: '',
        type: '',
        value: '',
        price_modifier: 0,
        sort_order: this.form.variants.length,
      });
    },

    onVariantOptionChange(index, optionId) {
      const opt = this.categoryOptionChoices.find((o) => o.id === optionId);
      if (!opt) return;

      const duplicate = this.form.variants.some((v, i) => i !== index && v.category_variant_option_id === optionId);
      if (duplicate) {
        window.toast('Opsi varian ini sudah ditambahkan.', 'warning');
        this.form.variants[index].category_variant_option_id = '';
        return;
      }

      this.form.variants[index] = {
        ...this.form.variants[index],
        category_variant_option_id: opt.id,
        type: opt.type,
        value: opt.value,
        price_modifier: opt.default_price_modifier,
      };
    },

    loadAllFromCategory() {
      if (!this.hasCategoryVariants) {
        window.toast('Kategori belum memiliki template varian.', 'warning');
        return;
      }

      this.form.variants = this.categoryOptionChoices.map((o, i) => ({
        category_variant_option_id: o.id,
        type: o.type,
        value: o.value,
        price_modifier: o.default_price_modifier,
        sort_order: i,
      }));
    },

    variantTypeLabel(type) {
      const t = (this.categoryVariantTemplate || []).find((row) => row.type === type);
      return t?.label || type;
    },

    removeVariant(i) {
      this.form.variants.splice(i, 1);
    },

    buildFormData() {
      const fd = new FormData();
      const f = this.form;
      fd.append('name', f.name);
      if (f.slug) fd.append('slug', f.slug);
      if (f.sku) fd.append('sku', f.sku);
      if (f.description) fd.append('description', f.description);
      fd.append('price_unit', f.price_unit);
      fd.append('base_price', String(f.base_price));
      ['app_price', 'web_price', 'inko_price', 'surang_price'].forEach((key) => {
        if (f[key] != null && f[key] !== '') {
          fd.append(key, String(f[key]));
        }
      });
      if (this.canEditCostPrice) {
        fd.append('cost_price', String(f.cost_price || 0));
      }
      fd.append('min_order', String(f.min_order));
      if (this.tracksStock) {
        fd.append('stock', String(Math.max(0, Number(f.stock) || 0)));
      }
      fd.append('status', f.status);
      fd.append('is_custom_size', f.is_custom_size ? '1' : '0');
      fd.append('surang_enabled', f.surang_enabled ? '1' : '0');
      if (f.is_custom_size && f.price_per_m2 != null) fd.append('price_per_m2', String(f.price_per_m2));
      if (f.is_custom_size) {
        ['app_price_per_m2', 'web_price_per_m2', 'surang_price_per_m2', 'inko_price_per_m2'].forEach((key) => {
          if (f[key] != null && f[key] !== '') {
            fd.append(key, String(f[key]));
          }
        });
      }
      if (f.category_id) fd.append('category_id', f.category_id);
      fd.append('reward_points', String(Math.max(0, Number(f.reward_points) || 0)));
      if (f.weight != null && f.weight !== '') fd.append('weight', String(f.weight));
      if (f.dimension_length != null && f.dimension_length !== '') fd.append('dimension_length', String(f.dimension_length));
      if (f.dimension_width != null && f.dimension_width !== '') fd.append('dimension_width', String(f.dimension_width));
      if (f.dimension_height != null && f.dimension_height !== '') fd.append('dimension_height', String(f.dimension_height));
      fd.append('variants', JSON.stringify(
        f.variants.filter((v) => v.category_variant_option_id || (v.type && v.value))
      ));
      if (this.imageFile) fd.append('main_image', this.imageFile);
      if (this.removeImage) fd.append('remove_image', '1');

      this.gallery.filter((g) => g._pending && g._file).forEach((g) => {
        fd.append('gallery_new[]', g._file);
      });

      const existingOrder = this.gallery
        .filter((g) => !g._pending)
        .map((g) => g.id);

      fd.append('gallery_remove', JSON.stringify(this.galleryRemoveIds));
      fd.append('gallery_order', JSON.stringify(existingOrder));

      if (this.galleryPrimaryId && !String(this.galleryPrimaryId).startsWith('tmp-')) {
        fd.append('gallery_primary', this.galleryPrimaryId);
      }

      return fd;
    },

    async save() {
      if (!this.form.name?.trim()) {
        window.toast('Nama produk wajib diisi.', 'warning');
        return;
      }
      if (this.form.price_unit === 'meter') {
        this.form.is_custom_size = true;
      }
      if (this.form.is_custom_size && !this.form.price_per_m2) {
        window.toast('Harga per m² wajib diisi untuk ukuran custom.', 'warning');
        return;
      }
      if (this.tracksStock && (this.form.stock === '' || this.form.stock == null || Number(this.form.stock) < 0)) {
        window.toast('Stok wajib diisi untuk satuan pcs, lembar, atau helai.', 'warning');
        return;
      }

      this.saving = true;
      try {
        const fd = this.buildFormData();
        const config = { headers: { 'Content-Type': 'multipart/form-data' } };

        if (this.productId) {
          fd.append('_method', 'PUT');
          await axios.post('/api/v1/kasir/products/' + this.productId, fd, config);
          window.toast('Produk diperbarui.', 'success');
          window.location.href = this.indexUrl;
        } else {
          const { data } = await axios.post('/api/v1/kasir/products', fd, config);
          const newId = data.data?.id;
          window.toast('Produk ditambahkan.', 'success');
          window.location.href = this.indexUrl;
        }
      } catch (e) {
        const errs = e.response?.data?.errors;
        const msg = e.response?.data?.message
          || (errs && Object.values(errs).flat()[0])
          || 'Gagal menyimpan.';
        window.toast(msg, 'error');
      } finally {
        this.saving = false;
      }
    },
  };
}

Alpine.data('kasirProductForm', createKasirProductForm);
